import type { IRegion } from '@intelligence/types/common';

import { getAlineaAnalyticsCalibrationPCConsoleTopChartsConfig } from '@intelligence/components/DownloadCommon/DownloadPage/common/conf/alineaAnalyticsCalibrationPCConsoleTopCharts';
import { buildDownloadData } from '@intelligence/utils/commonUtils';
import { getTrueDataMetricText } from '@intelligence/components/PublicTrueData/utils/utils';
import { normalizeAlineaTrueDataRows } from '../../../utils/alineaTrueData';
import { buildTrueDataComparator } from './index';

import type {
  ITrueDataHeaderLabels,
  IPublicFeedbackParams,
  ISortConfig,
  TTrueDataMode,
  TTrueDataSourceLabels,
} from '../types';

export { normalizeAlineaTrueDataRows } from '../../../utils/alineaTrueData';

export interface ITrueDataDownloadResponse {
  list: IRecord[];
  total: number;
  [key: string]: unknown;
}

export interface ITrueDataSheet {
  sheetName: string;
  data?: Array<Array<unknown>>;
}

export interface ITrueDataDownloadSheetParams {
  mode: TTrueDataMode;
  rows: IRecord[];
  feedBackParams?: IPublicFeedbackParams;
  sortConfig: ISortConfig;
  headerLabels: ITrueDataHeaderLabels;
  sourceTypeLabels: TTrueDataSourceLabels;
  formatMessage: IFormatMessage;
  gameName?: string;
  regionsCountries?: IRegion[];
}

/**
 * DataBrain 接口的 source_type 可能为空；这里沿用真数表格使用的默认来源，
 * 让表格和下载消费同一份标准化字段，而不是在两个展示入口分别兜底。
 */
export const normalizeDataBrainTrueDataRows = (histories: IRecord[] = []): IRecord[] => {
  return histories.map((history) => ({
    ...history,
    source_type: history?.source_type || 'Public Data',
  }));
};

/**
 * 将真数接口的 history/count 响应统一为导出层消费的 list/total 结构。
 * 接口返回字段的差异只在这里处理，导出配置无需了解 DataBrain/Alinea 的响应结构。
 */
export const normalizeTrueDataDownloadResponse = (response: IRecord = {}): ITrueDataDownloadResponse => {
  const responseData = response?.data && typeof response.data === 'object' ? response.data : {};
  const history = Array.isArray(responseData.history) ? responseData.history : [];
  const responseList = Array.isArray(responseData.list) ? responseData.list : [];
  const list = responseList.length ? responseList : history;
  const rawTotal = responseData.total ?? responseData.count;
  const total = typeof rawTotal === 'number' ? rawTotal : Number(rawTotal) || list.length;

  return {
    ...responseData,
    list,
    total,
  };
};

/**
 * 下载沿用真数表格的排序规则，先按 Metrics 分组，再按当前排序字段排列组内记录。
 * 这样接口返回顺序变化时，导出文件仍与弹窗展示保持一致。
 */
const sortTrueDataRows = (
  rows: IRecord[],
  mode: TTrueDataMode,
  sortConfig: ISortConfig,
): IRecord[] => {
  const comparator = buildTrueDataComparator(mode, {
    orderMetric: sortConfig.sortKey,
    order: sortConfig.sortVal,
  });

  return [...rows].sort(comparator);
};

const buildDataBrainSheets = (
  rows: IRecord[],
  headerLabels: ITrueDataHeaderLabels,
  sourceTypeLabels: TTrueDataSourceLabels,
  formatMessage: IFormatMessage,
): Record<string, ITrueDataSheet> => {
  return {
    stat: {
      sheetName: 'Stats',
      data: buildDownloadData(
        [
          { title: headerLabels?.gameName || 'Game Name', key: (item: IRecord) => item?.game_name || '' },
          {
            title: headerLabels?.date || 'Date',
            key: (item: IRecord) => {
              const startDate = item?.metric_start_date;
              const endDate = item?.metric_end_date;
              if (startDate === 'once') {
                return endDate || '';
              }
              if (!startDate) {
                return '';
              }
              return startDate === endDate ? startDate : `${startDate}~${endDate || ''}`;
            },
          },
          {
            title: headerLabels?.dataSource || 'Data Source',
            key: (item: IRecord) => sourceTypeLabels?.[item?.source_type] || item?.source_type || '',
          },
          {
            title: headerLabels?.metrics || 'Metrics',
            key: (item: IRecord) => {
              return item?.metric_name
                ? getTrueDataMetricText(item.metric_name, { source: 'databrain', formatMessage })
                : '';
            },
          },
          { title: headerLabels?.dataBrainValue || 'Data', key: (item: IRecord) => item?.metric_value ?? '' },
        ],
        rows,
      ),
    },
  };
};

/**
 * 按来源生成统一的 Sheet 结构；来源差异只保留在各自已有的数据配置中。
 */
export const buildTrueDataSheets = (params: ITrueDataDownloadSheetParams): Record<string, ITrueDataSheet> => {
  const {
    mode,
    rows,
    feedBackParams,
    sortConfig,
    headerLabels,
    sourceTypeLabels,
    formatMessage,
    gameName,
    regionsCountries,
  } = params;
  const normalizedRows = mode === 'alinea'
    ? normalizeAlineaTrueDataRows(rows)
    : normalizeDataBrainTrueDataRows(rows);
  const sortedRows = sortTrueDataRows(normalizedRows, mode, sortConfig);
  const downloadParams = {
    ...feedBackParams,
    order_metric: sortConfig.sortKey,
    order: sortConfig.sortVal,
    mode,
    gameName,
    regionOptions: regionsCountries,
    regionsCountries,
  };

  let sheets: Record<string, ITrueDataSheet>;

  if (mode === 'alinea') {
    const alineaRows = sortedRows.map((item) => ({
      ...item,
      name: item?.name || item?.entity_name || gameName || '',
    }));
    const alineaParams = {
      ...downloadParams,
      // 已由真数 comparator 排好分组；关闭公共配置的二次日期排序，避免重新打散 Metrics。
      order_metric: '',
      company_show: 0,
      genre_show: 0,
      release_date_show: 1,
      trueDataHeaderLabels: headerLabels,
    };

    sheets = getAlineaAnalyticsCalibrationPCConsoleTopChartsConfig(
      { list: alineaRows },
      alineaParams,
      alineaParams,
    ) as Record<string, ITrueDataSheet>;
  } else {
    sheets = buildDataBrainSheets(sortedRows, headerLabels, sourceTypeLabels, formatMessage);
  }

  const { summary: summarySheet, ...sheetsWithoutSummary } = sheets;
  return {
    ...sheetsWithoutSummary,
    stat: {
      ...sheetsWithoutSummary.stat,
      sheetName: mode === 'alinea' ? 'Alinea' : 'DataBrain True Data',
    },
  };
};
